// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Global tour context — keeps TourRunner mounted at layout level
 * so tours survive route navigation between modules.
 */

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { Permission, type TourScript } from '@vn-grc/shared';
import type { TourMode } from '../hooks/useTourEngine.js';
import { usePermissionContext } from './PermissionContext.js';
import { useModuleConfig } from './ModuleConfigContext.js';
import { prepareTourForAccess } from '../utils/prepare-tour-for-access.js';

interface TourContextValue {
  activeScript: TourScript | null;
  activeTourMode: TourMode;
  startStepIndex: number;
  /** Monotonic counter — used as React key to force TourRunner remount. */
  launchId: number;
  launchTour: (script: TourScript, mode: TourMode, startStep?: number) => boolean;
  /**
   * Whether a tour is running NOW. Reads the same synchronous ref launchTour
   * checks, so an async caller can ask again after an await, when the
   * `activeScript` it rendered with may already be stale.
   */
  isTourActive: () => boolean;
  prepareTour: (script: TourScript) => TourScript | null;
  clearTour: () => void;
  setActiveTourMode: (mode: TourMode) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const { hasPermission } = usePermissionContext();
  const { isModuleEnabled } = useModuleConfig();
  const [activeScript, setActiveScript] = useState<TourScript | null>(null);
  const [activeTourMode, setActiveTourMode] = useState<TourMode>(
    (localStorage.getItem('grc_tour_mode') as TourMode) || 'autoplay',
  );
  const [startStepIndex, setStartStepIndex] = useState(0);
  const [launchId, setLaunchId] = useState(0);
  const counterRef = useRef(0);
  // fix-sprint-20261025-2133 F9 — written synchronously on launch and clear so
  // two launches in the same tick cannot both pass the "no tour active" check.
  const activeScriptRef = useRef<TourScript | null>(null);

  const prepareTour = useCallback(
    (script: TourScript) => prepareTourForAccess(script, hasPermission, isModuleEnabled),
    [hasPermission, isModuleEnabled],
  );

  const launchTour = useCallback(
    (script: TourScript, mode: TourMode, startStep = 0) => {
      // One tour at a time: a running tour is finished or exited (clearTour)
      // before another can start, from any entry point.
      if (activeScriptRef.current) return false;
      if (
        !hasPermission(Permission.ONBOARDING_TOUR_READ) ||
        !hasPermission(Permission.ONBOARDING_TOUR_WRITE)
      )
        return false;
      const permitted = prepareTour(script);
      if (!permitted) return false;
      counterRef.current += 1;
      activeScriptRef.current = permitted;
      setActiveScript(permitted);
      setActiveTourMode(mode);
      // Numeric progress from an older revision cannot reconstruct a new workflow.
      setStartStepIndex(script.revision ? 0 : startStep);
      setLaunchId(counterRef.current);
      return true;
    },
    [hasPermission, prepareTour],
  );

  const isTourActive = useCallback(() => activeScriptRef.current !== null, []);

  const clearTour = useCallback(() => {
    activeScriptRef.current = null;
    setActiveScript(null);
    setStartStepIndex(0);
  }, []);

  return (
    <TourContext.Provider
      value={{
        activeScript,
        activeTourMode,
        startStepIndex,
        launchId,
        launchTour,
        isTourActive,
        prepareTour,
        clearTour,
        setActiveTourMode,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTourContext(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTourContext must be used within TourProvider');
  return ctx;
}
